import S, { DataSignal } from 's-js';
import { SArray as ArraySignal } from 's-array';
import * as _ from 'underscore';
import * as Promise from 'bluebird';
import { IAjax, Request } from 'lib/Ajax';
import TZDate from 'lib/TZDate';
import { Entrypoints } from 'core/app/entrypoints';
import { DomainSummary } from 'data/models/DomainSummary';
import { PCUser } from 'data/models/PCUser';
import { User } from 'data/models/User';
import { Role } from 'data/models/Role';
import { UserRole, UserRoleData } from 'data/models/UserRole';
import { UserPermissionsResponse, UserPermissionsResponseData } from 'data/models/UserPermissionsResponse';
import { PCCache } from 'data/PCCache';
import { PCRoles } from 'data/models/PCRoles';
import { RoleMapper } from 'common/services/rolemapper';
import { UserPermissionsCache } from './cache';

export interface PCUserViewModel {
    Model : PCUser;
    User : () => UserViewModel | null;
    ConfirmDeleteUser : () => void;
}

export interface UserViewModel {
    Model : User;
    HasOrImpliesRole : (roleId : number) => boolean;
    ImpliesOrMaterializesRole : (roleId : number) => boolean;
    ToggleRole : (roleId : number) => void;
}

export interface UserRoleDraft {
    Role : Role;
    DraftStatus : DataSignal<boolean>;
    Add : () => void;
    Remove : () => void;
}

export interface DeleteUserViewModel {
    User : PCUser;
    ActionSuccess : () => boolean;
    Delete : () => void;
    HideModal : () => void;
}

export interface UserPermissionsParams {
    organizationId? : number, // TH: positive to indicate a single org, 0 or missing to indicate all orgs
    onHidePopup : () => void
}

export function UserPermissions(
    ajax : IAjax, 
    entrypoints : Entrypoints, 
    cache : PCCache, 
    queryCache : UserPermissionsCache,
    domain : DomainSummary, 
    { organizationId, onHidePopup } : UserPermissionsParams
    ) {
    let model = queryCache.UserPermissionsResponses.update1(null, { OrganizationId: organizationId || 0 }),
        url = entrypoints.UserPermissions({ Id: organizationId || 0 }),
        request = ajax.Url<UserPermissionsResponseData>(url).Get(),
        canAssignSiteAdmin = () => model.AssignableRoles().some(r => r.Id() == PCRoles.SiteAdmin),
        selectedForDeleteUser = S.data<PCUser | null>(null),
        deleteUserModal = S(() => selectedForDeleteUser() && DeleteUserViewModel(selectedForDeleteUser()!));

    request.done(data => {
        queryCache.UserPermissionsResponses.update1(request.Date, data);
    });

    return {
        Model: model,
        Users: model.Users.map(user => PCUserViewModel(user, model.AssignableRoles)),
        CanAssignSiteAdmin: canAssignSiteAdmin,
        HidePopup: onHidePopup,
        DeleteUserModal: deleteUserModal,
        HasPilots: () => domain.HasActivePilots()
    };

    function PCUserViewModel(user : PCUser, assignableRoles : ArraySignal<Role>) : PCUserViewModel {
        let _user = S(() => user.User() && UserViewModel(user.User()!, assignableRoles));

        return {
            Model: user,
            User: _user,
            ConfirmDeleteUser: () => selectedForDeleteUser(user)
        };
    }

    function UserViewModel(u : User, assignableRoles : ArraySignal<Role>) : UserViewModel {
        let roleDrafts = assignableRoles.map(r => UserRoleDraft(u, r)),
            draftRoleIds = roleDrafts.filter(draft => draft.DraftStatus()).map(draft => draft.Role.Id()),
            otherAssignedIds = u.Roles.filter(ur => !assignableRoles().some(r => ur.Role() === r)).map(ur => ur.RoleId()),
            roleMapper = RoleMapper(draftRoleIds.concat(otherAssignedIds));

        return {
            Model : u,
            HasOrImpliesRole: roleMapper.HasOrImpliesRole,
            ImpliesOrMaterializesRole: roleMapper.ImpliesOrMaterializesRole,
            ToggleRole: (roleId : number) => roleMapper.ToggleRole(modifyRole(join => join.Add()), modifyRole(join => join.Remove()), roleId)
        };

        function modifyRole(action : (join : UserRoleDraft) => void) {
            return (roleId : number) => {
                let draft = findRoleDraft(roleId);
                if (!draft) return;
                action(draft);
            };
        }

        function findRoleDraft(roleId : number) : UserRoleDraft | undefined {
            return S.sample(() => _.find(roleDrafts(), draft => draft.Role.Id() === roleId));
        }
    }

    // Maintain separate draft state and persisted state for UserRoles
    function UserRoleDraft(u : User, r : Role) : UserRoleDraft {
        let client = ajax.Url<{}, UserRoleData>(() => (u.RolesUrlBase() || '').replace('{RoleId}', '' + r.Id())),
            key = () => UserRole.dataKey({
                UserId: u.Id(),
                RoleId: r.Id()
            }),
            persistedUserRole = cache.UserRoles.index(key),
            persistedStatus = () => !!persistedUserRole(),
            draftStatus = S.data(false),
            dirty = S.data(false),
            userChanges = false; // Has the user made changes since the last request was sent
        
        S(() => {
            if (!dirty()) draftStatus(persistedStatus());
        });

        return {
            Role: r,
            DraftStatus: draftStatus,
            Add: Add,
            Remove: Remove
        };

        function Add() {
            userChanges = true;
            S.freeze(() => {
                draftStatus(true);
                if (!S.sample(dirty)) {
                    dirty(true);
                    sendPutRequest();
                }
            });
        }

        function Remove() {
            userChanges = true;
            S.freeze(() => {
                draftStatus(false);
                if (!S.sample(dirty)) {
                    dirty(true);
                    sendDeleteRequest();
                }
            });
        }

        function sendPutRequest() {
            wrapRequest(() => {
                let req = client.Put({});
                return req.then(data => {
                    cache.UserRoles.update1(req.Date, data)
                });
            });
        }

        function sendDeleteRequest() {
            wrapRequest(() => {
                let req = client.Delete();
                return req.then(() => {
                    cache.UserRoles.update(req.Date, [], [S.sample(key)]);
                });
            });
        }

        function wrapRequest(req : () => Promise<void>) {
            userChanges = false;
            req().finally(() => {
                let _draftStatus = S.sample(draftStatus),
                    _persistedStatus = S.sample(persistedStatus);
                if (userChanges && _draftStatus !== _persistedStatus) {
                    _draftStatus ? sendPutRequest() : sendDeleteRequest();
                } else {
                    dirty(false);
                    userChanges = false;
                }
            });
        }
    }

    function DeleteUserViewModel(user : PCUser) : DeleteUserViewModel {
        let action = S.data<Request<PCUser> | null>(null),
            actionSuccess = () =>
                !!(action() && action()!.Response() && action()!.Response()!.Success);

        return {
            User: user,
            ActionSuccess: actionSuccess,
            Delete: Delete,
            HideModal: () => selectedForDeleteUser(null)
        };

        function Delete() {
            user.Active(false);
            let req = ajax.Url<PCUser>(() => user.Url() || '').Put(user);
            req.then(() => model.Users.remove(user));
            action(req);
        }
    }
}
export type UserPermissions = ReturnType<typeof UserPermissions>;


// WEBPACK FOOTER //
// ./apps/portcall/userpermissions/model.ts